import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

const DEFAULT_DAY_BOUNDARY_HOUR = 5

/**
 * Returns the configured "business day boundary hour" — the wall-clock
 * hour (0–12, default 5) at which the operational day rolls over.
 *
 * Used by Reports to render the "late-night orders before X:00 AM are
 * counted in yesterday's business day" hint on the Daily Sales tab.
 *
 * Mirrors the backend `util.DayBoundaryHour()` source of truth: the
 * value is stored as an integer in `app_settings.day_boundary_hour` and
 * applied at order creation time via dayops.EnsureOpenDayForOrder. Out
 * of range values fall back to the documented default of 5 so a corrupt
 * setting can't break the UI.
 *
 * Cache key matches every other consumer of `apiClient.getAllSettings()`
 * so no extra round-trip happens — we piggyback on the request the
 * sidebar / currency context / business-name hook already make.
 */
export function useDayBoundaryHour(): number {
  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  const raw = settingsRes?.data as Record<string, unknown> | undefined
  const value = raw?.day_boundary_hour
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 12) {
    return value
  }
  return DEFAULT_DAY_BOUNDARY_HOUR
}
